import { Button, CloseableContent, ConfirmationPrompt, Icon, Modal, Notification, STATUS_TYPES } from 'dyl-components'
import React, { useState, useEffect } from 'react'
import { useController, useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { Form, Header, Popup } from 'semantic-ui-react'
import sequencesActions from 'actions/sequences';
import contactSequenceActions from 'actions/contact_sequence';
import sequenceTasksActions from 'actions/sequence_tasks';
import { Modal as SemanticModal } from "semantic-ui-react"
import historyActions from 'actions/history';

export default function SequenceModal({ isModalOpen, modalRef, onClose, contact_id, refresh }) {
    const [isConfirmingModification, setIsConfirmingModification] = useState(false)

    const onCancelRemoval = () => { setIsConfirmingModification(false); };
    const { control, formState: { isDirty }, reset, handleSubmit } = useForm({
        mode: 'onChange',
        defaultValues: {
            sequence: ''
        },
    });
    const {
        sequences,
        selectedSequenceId,
        isUpdatingContactSequence,
        pipeline_id,
        account_stage,
        pipeline_sequence_id,
        isReadingSequenceContactTasks
    } = useSelector((state) => ({
        sequences: state.sequences.sequencesShort || [],
        selectedSequenceId: state.sequence_tasks.selectedSequenceId,
        isUpdatingContactSequence: state.sequences.isAddingContactToSequence || state.contact_sequence.isRemovingSequence,
        pipeline_id: state.contact.current_stage?.pipeline_id,
        pipeline_sequence_id: state.contact.current_stage?.sequence_id,
        account_stage: state.contact.current_stage?.account_stage,
        isReadingSequenceContactTasks: state.sequence_tasks.isReadingSequenceContactTasks
    }));

    const sequenceOptions = sequences.map((sequence) => ({
        key: sequence.id,
        value: sequence.id,
        text: sequence.name
    }));

    const dispatch = useDispatch();

    useEffect(() => {
        if (isModalOpen) {
            dispatch(sequencesActions.readShort({ limit: 2500 }));
            dispatch(sequenceTasksActions.readContactTasks(contact_id)).then(data => {
                reset({ sequence: data?.workflow_id || '' });
            });
        }
    }, [isModalOpen, contact_id, dispatch, reset]);

    const onSave = handleSubmit(async (data) => {
        const { sequence } = data;
        try {
            const getSeqName = (id) =>
                sequences?.find((seq) => seq?.id === id)?.name ?? null;

            const actionType = (() => {
                if (selectedSequenceId) {
                    if (sequence) {
                        return "changed";
                    }
                    return "removed";
                }
                return "added";
            })()
            if (selectedSequenceId) {
                await dispatch(
                    contactSequenceActions.removeFromContact(Number(contact_id), {
                        pipeline_id,
                        account_stage,
                        pipeline_sequence_id
                    })
                );
            }

            if (sequence) {
                await dispatch(
                    sequencesActions.addToSequence(
                        { contact_id: Number(contact_id) },
                        null,
                        sequence
                    )
                );
            }

            await dispatch(
                historyActions.createHistory(
                    {
                        activity_type: "sequence",
                        action: actionType,
                        from:
                            actionType === "added"
                                ? null
                                : getSeqName(selectedSequenceId),
                        to:
                            actionType === "removed"
                                ? null
                                : getSeqName(sequence)
                    },
                    "",
                    contact_id
                )
            );
            Notification.alert(`Sequence successfully ${actionType !== 'changed' ? actionType : 'updated'}!`, STATUS_TYPES.SUCCESS);
            reset();
            onClose();
            refresh();
        }
        catch (error) {
            console.error(error);
            Notification.alert('Failed to save sequence!', STATUS_TYPES.ERROR);
        }
    });

    const { field: sequenceField } = useController({
        name: "sequence",
        control
    });

    const willRemoveSequence = sequenceField.value === '' && selectedSequenceId;

    return (
        <React.Fragment>
            <Popup
                content={
                    <CloseableContent onClose={() => { onClose(); reset(); }}>
                        <React.Fragment>
                            <Header color="primary">Sequences</Header>
                            <Form>
                                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
                                    <Form.Select
                                        placeholder="Select Sequence Name"
                                        label="Sequence Name"
                                        name={sequenceField.name}
                                        value={sequenceField.value}
                                        options={sequenceOptions}
                                        onChange={(_, { value }) => {
                                            sequenceField.onChange({ target: { name: sequenceField.name, value } });
                                        }}
                                        required
                                        search
                                        loading={isReadingSequenceContactTasks}
                                        width={13}
                                        disabled={isUpdatingContactSequence || isReadingSequenceContactTasks}
                                    />
                                    {sequenceField.value && (
                                        <Form.Field as='div'>
                                            <Icon
                                                link
                                                className="fa-solid fa-trash redIcon"
                                                style={{ marginLeft: 10, marginTop: 10 }}
                                                onClick={() => {
                                                    sequenceField.onChange({ target: { name: sequenceField.name, value: '' } });
                                                }}
                                                disabled={isUpdatingContactSequence || isReadingSequenceContactTasks}
                                            />
                                        </Form.Field>
                                    )}
                                </div>
                                <Button type="button" onClick={(e) => {
                                    if (selectedSequenceId) {
                                        setIsConfirmingModification(true)
                                    } else {
                                        handleSubmit(onSave)(e)
                                    }
                                }} style={{ float: 'right' }} disabled={!isDirty} loading={isUpdatingContactSequence}>
                                    Save
                                </Button>
                            </Form>
                        </React.Fragment>
                    </CloseableContent>
                }
                open={isModalOpen}
                position="bottom left"
                style={{ width: '400px', zIndex: "1000" }}
                flowing
                context={modalRef}
                onClose={onClose}
                closeOnDocumentClick={false}
                closeOnEscape={false}
                closeOnTriggerBlur={false}
            />

            <SemanticModal className='ConfirmModal' size='mini' open={isConfirmingModification} onClose={onCancelRemoval} style={{ zIndex: "10000" }}>
                <Modal.Content>
                    <ConfirmationPrompt header={'Are you sure?'} subheader={`All upcoming sequence tasks will be ${willRemoveSequence ? "removed" : "replaced"}`} onCancel={onCancelRemoval} onConfirm={() => {
                        setIsConfirmingModification(false)
                        handleSubmit(onSave)()
                    }} />
                </Modal.Content>
            </SemanticModal>
        </React.Fragment>
    );
}
